/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * SoonKi Min (skmin@rsupport.com)
 *
 * @description
 *
 * @created at Fri Apr 05 2024
 **/

import { forwardRef, Fragment, memo, useEffect } from "react";
import { isEmpty } from "lodash-es";

import { Maybe, Choice } from "@/frameworks/libraries/components";
import { MessageList } from "@/frameworks/core/chat";
import { Configure } from "@/frameworks/libraries";

import { ChatItem } from "@/modules/chat/components/SidebarContents";
import { DateDescriptor, System } from "../../..";
import { checkIsMessageGroup } from "../utils";

type MessageProps = {
	message: MessageList[string];
	variant: ChatUI.Size;
	index: number;
	isMyRoom: boolean;
	previousMessage: MessageList[string] | null;
	spaceID?: string;
	myRoomID?: string;
	isLastChat: boolean;
	isShowLastChat: boolean;
	isFirstRender: boolean;
	hasNextPage?: boolean;
	clientMessageId?: string;
	thread?: { count: number; messageLastSentTime: number };
	threadID?: string;
	chatSearchTextId?: string;
	onMount?: () => void;
	onUnMount?: () => void;
	onMesageRendered?: () => void;
	onNewMesageRendered?: () => void;
	highlightOnLoad?: boolean;
};

const Message = memo(
	forwardRef<HTMLLIElement, MessageProps>(
		({ message, variant, index, previousMessage, onMount, onUnMount, highlightOnLoad, ...rest }, ref) => {
			const contents = ContentParser(message.content);
			const key = contents as ChatServices.Format2.Container.System;
			const isMessageGroup = checkIsMessageGroup(previousMessage, message);

			const scrollIntoView = () => {
				if (typeof ref !== "function" && !rest.chatSearchTextId) {
					ref?.current?.scrollIntoView({ behavior: "instant" });

					if (highlightOnLoad && ref?.current) {
						const element = ref.current;
						element.classList.remove("flash-animation");
						void element.offsetWidth;
						element.classList.add("flash-animation");
					}
				}
			};

			useEffect(() => {
				if (!rest.isLastChat) return;
				if (rest.isFirstRender) {
					scrollIntoView();
				} else {
					if (rest.isShowLastChat || message.user?.id === Configure.userID) {
						scrollIntoView();
					}
				}
			}, []);

			useEffect(() => {
				onMount?.();
				return () => onUnMount?.();
			}, []);

			useEffect(() => {
				if (rest.isFirstRender) rest.onMesageRendered?.();
				else if (rest.chatSearchTextId === message.roomMessageId) rest.onNewMesageRendered?.();
			}, [rest.chatSearchTextId, message.roomMessageId, rest.isFirstRender]);

			if (isEmpty(key)) return;
			return (
				<Fragment>
					<Maybe test={variant !== "transparent"}>
						<DateDescriptor
							isFirstMessage={!rest.hasNextPage && !rest.spaceID && index === 1}
							timestamp1={message.createdAt}
							timestamp2={index > 0 ? previousMessage?.createdAt ?? 0 : 0}
							isCreate={key?.elements[0]?.key !== "create"}
						/>
					</Maybe>
					<Choice value={contents.type} key={message.roomMessageId}>
						<Choice.Case test={"system"}>
							<System
								variant={variant}
								contents={contents as ChatServices.Format2.Container.System}
								ref={ref}
								{...message}
							/>
						</Choice.Case>
						<Choice.CaseElse>
							<ChatItem
								{...message}
								{...rest}
								isMe={message.user?.id === Configure.userID}
								isMessageGroup={isMessageGroup}
								variant={variant}
								ref={ref}
								content={contents as ChatServices.Format2.Message}
							/>
						</Choice.CaseElse>
					</Choice>
				</Fragment>
			);
		}
	),
	(prevProps, nextProps) => {
		const prevMsg = prevProps.message;
		const nextMsg = nextProps.message;

		return (
			prevMsg.roomMessageId === nextMsg.roomMessageId &&
			prevMsg.reactions === nextMsg.reactions &&
			!nextMsg.messageUpdated &&
			prevMsg.replyMessage == nextMsg.replyMessage &&
			prevMsg.thread?.count == nextMsg.thread?.count &&
			prevMsg.deleted == nextMsg.deleted
		);
	}
);

const ContentParser = (content: string): ChatServices.Format2.All => {
	return content
		? JSON.parse(content)
		: {
				type: "richText",
				elements: [
					{
						type: "text",
						text: ""
					}
				]
			};
};

export default Message;
